import { ArrowRight, Phone } from "lucide-react";
import { SITE } from "@/lib/site";
import Reveal from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1625801882109-032ad88edeb3?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400";

export default function Hero() {
  const goContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      id="hero"
      className="relative min-h-screen w-full overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Luxury bathroom"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080A0B]/95 via-[#080A0B]/55 to-[#080A0B]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080A0B] via-transparent to-transparent" />
      </div>

      <div className="relative z-10 pt-40 md:pt-44 pb-20 px-6 md:px-12 lg:px-16 min-h-screen flex flex-col justify-between">
        <div className="max-w-5xl">
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <span className="block w-10 h-px bg-[#3FB8DC]" />
              <span className="eyebrow">
                {SITE.established} · {SITE.suburb}, {SITE.region}
              </span>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h1 className="font-serif-display text-[clamp(2.6rem,7vw,7rem)] leading-[0.95] tracking-tight text-[#F4F0EB]">
              Sydney's
              <br />
              <span className="italic text-[#A1A1AA]">Trusted Plumbing </span>
              <span className="text-[#3FB8DC]">Specialists.</span>
            </h1>
          </Reveal>

          <Reveal delay={260}>
            <p className="mt-10 max-w-xl text-base md:text-lg text-white leading-relaxed">
              MarX Plumbing was built on a simple philosophy: show up when you say
              you will, do the job properly and take pride in every detail. From
              architecturally designed homes and high-end renovations to commercial
              maintenance and emergency repairs, we provide the quality, reliability
              and service our clients have come to trust.
            </p>
          </Reveal>

          <Reveal delay={380}>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-quote-btn"
                onClick={goContact}
                className="btn-primary"
              >
                Request a Quote
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href={SITE.phoneHref}
                data-testid="hero-call-btn"
                className="btn-secondary"
              >
                <Phone className="w-4 h-4 text-[#3FB8DC]" />
                {SITE.phone}
              </a>
            </div>
          </Reveal>

          <Reveal delay={480}>
            <a
              href={SITE.phoneHref}
              data-testid="hero-emergency-btn"
              className="mt-6 inline-flex items-center gap-4 bg-[#3FB8DC] text-[#080A0B] pl-5 pr-6 py-3 hover:bg-[#F4F0EB] transition-colors group"
            >
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#080A0B] opacity-60" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#080A0B]" />
              </span>
              <span className="eyebrow !text-[#080A0B]" style={{ fontSize: "0.6rem" }}>
                24/7 Emergency Callout
              </span>
              <span className="hidden sm:inline text-sm">
                Burst pipe · blocked drain · no hot water — call now
              </span>
              <Phone className="w-4 h-4 ml-1" />
            </a>
          </Reveal>
        </div>

        <Reveal delay={520}>
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 max-w-4xl border-t border-[#27272A]/60 pt-8">
            {[
              { n: "10+", l: "Years on the tools" },
              { n: "320+", l: "Homes serviced" },
              { n: "472396C", l: "NSW Licensed" },
              { n: "24/7", l: "Emergency line" },
            ].map((s) => (
              <div key={s.l} data-testid={`hero-stat-${s.l.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="font-serif-display text-3xl md:text-4xl text-[#F4F0EB]">{s.n}</div>
                <div className="eyebrow mt-2" style={{ fontSize: "0.6rem" }}>
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
